import {
    FoodsIcon,
    FoodsIconButton,
    SearchBox,
    Typography as FoodsTypography,
    type IconMapping,
} from "@foodpilot/foods";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import {
    Box,
    CardActionArea,
    Dialog,
    DialogContent,
    DialogTitle,
    Stack,
    Typography as MuiTypography,
    useTheme,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { TransportLeg } from "src/api/client-api/transport/useTransportLegs";
import { useSearchTransportModes } from "src/api/sure-api/useSearchTransportModes";
import { getTransportLegModeIcons } from "src/utils/transport/modeIcons";
import { getTransportLegMeta } from "src/utils/transport/resourceMeta";
import { ReferenceSearchDialog, SelectionGrid, type SelectionGridColumn } from "src/components/ReferenceSearch";
import { referenceSearchPrimaryNameSx } from "src/components/ReferenceSearch/referenceSearchTextStyles";
import { TransportResourceCard } from "src/components/Transport/TransportResourceCard";

type TransportAddLegModalProps = {
    appearance?: "legacy" | "product-v2";
    open: boolean;
    legs: TransportLeg[];
    onClose: () => void;
    onAdd: (leg: TransportLeg) => void;
};

type TransportModeTagProps = {
    icon: keyof IconMapping | "flightTakeoff";
    label: string;
};

const TransportModeTag = ({ icon, label }: TransportModeTagProps) => {
    const theme = useTheme();

    return (
        <Box
            component="span"
            data-testid="transport-mode-tag"
            sx={{
                alignItems: "center",
                backgroundColor: theme.custom.grey[400],
                borderRadius: theme.spacing(0.5),
                boxSizing: "border-box",
                color: theme.custom.grey[2500],
                display: "inline-flex",
                gap: theme.spacing(0.25),
                height: theme.spacing(3),
                padding: theme.spacing(0.25, 1, 0.25, 0.25),
                whiteSpace: "nowrap",
            }}
        >
            <Box
                aria-hidden="true"
                component="span"
                sx={{ alignItems: "center", display: "inline-flex", padding: theme.spacing(0.5) }}
            >
                {icon === "flightTakeoff" ?
                    <FlightTakeoffIcon data-testid="transport-mode-flight-icon" sx={{ fontSize: theme.spacing(2) }} />
                :   <FoodsIcon color={theme.custom.grey[2500]} icon={icon} size={1} />}
            </Box>
            <FoodsTypography component="span" variant="body-bold" sx={{ color: "inherit", lineHeight: 1.6 }}>
                {label}
            </FoodsTypography>
        </Box>
    );
};

export const TransportAddLegModal = ({
    appearance = "product-v2",
    open,
    legs,
    onClose,
    onAdd,
}: TransportAddLegModalProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [search, setSearch] = useState("");
    const { transportModes } = useSearchTransportModes({ enabled: open });
    const filteredLegs = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();

        return legs
            .toSorted((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
            .filter((leg) => {
                if (normalizedSearch === "") return true;

                return [
                    leg.name,
                    leg.originLabel,
                    leg.destinationLabel,
                    leg.originCountryCode,
                    leg.destinationCountryCode,
                    leg.transportModeId?.toString(),
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase()
                    .includes(normalizedSearch);
            });
    }, [legs, search]);
    const getLegPresentation = (leg: TransportLeg) => {
        const title = leg.name ?? `${t("transport.resource.kind.leg", "Leg")} #${leg.id}`;
        const origin = leg.originLabel ?? leg.originCountryCode ?? "-";
        const destination = leg.destinationLabel ?? leg.destinationCountryCode ?? "-";
        const mode = getTransportLegModeIcons(leg, transportModes)[0];
        const accessibleLabel = [title, `${origin} → ${destination}`, ...getTransportLegMeta(leg, t), mode.label].join(
            " — ",
        );

        return { accessibleLabel, mode, title };
    };
    const columns: SelectionGridColumn<TransportLeg>[] = [
        {
            id: "name",
            width: "50%",
            render: (leg) => {
                const { accessibleLabel, title } = getLegPresentation(leg);

                return (
                    <FoodsTypography title={accessibleLabel} variant="body-bold" sx={referenceSearchPrimaryNameSx}>
                        {title}
                    </FoodsTypography>
                );
            },
        },
        {
            id: "mode",
            width: "50%",
            render: (leg) => {
                const { mode } = getLegPresentation(leg);

                return <TransportModeTag icon={mode.icon} label={mode.label} />;
            },
        },
    ];

    if (appearance === "legacy") {
        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    "data-appearance": "legacy",
                    sx: {
                        maxWidth: theme.spacing(92),
                        borderRadius: theme.spacing(1.5),
                        backgroundColor: theme.custom.grey[100],
                    },
                }}
            >
                <DialogTitle
                    component="div"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        minHeight: theme.spacing(8),
                        p: theme.spacing(2, 2, 2, 3),
                        borderBottomColor: theme.custom.grey[500],
                        borderBottomStyle: "solid",
                        borderBottomWidth: 1,
                    }}
                >
                    <MuiTypography component="h2" variant="h5">
                        {t("transport.circuits.add_leg", "Add leg")}
                    </MuiTypography>
                    <FoodsIconButton
                        size="m"
                        icon="close"
                        tooltip={{ text: t("common.close", "Close") }}
                        buttonProps={{ onClick: onClose, "aria-label": t("common.close", "Close") }}
                    />
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    <Stack gap={2} sx={{ p: 2 }}>
                        <SearchBox
                            variant="standalone"
                            value={search}
                            placeholder={t("transport.add_leg.search_placeholder", "Search for a leg")}
                            onChange={setSearch}
                        />
                    </Stack>

                    <Stack
                        gap={1}
                        sx={{
                            borderTopColor: theme.custom.grey[500],
                            borderTopStyle: "solid",
                            borderTopWidth: 1,
                            maxHeight: theme.spacing(52.5),
                            overflow: "auto",
                            p: 2,
                        }}
                    >
                        {filteredLegs.length === 0 && (
                            <Box py={4} textAlign="center">
                                <MuiTypography variant="body2" sx={{ color: theme.custom.grey[2000] }}>
                                    {t("transport.add_leg.empty", "No leg matches the current search.")}
                                </MuiTypography>
                            </Box>
                        )}

                        {filteredLegs.map((leg) => {
                            const { accessibleLabel, title } = getLegPresentation(leg);

                            return (
                                <CardActionArea
                                    key={leg.id}
                                    aria-label={accessibleLabel}
                                    onClick={() => {
                                        onAdd(leg);
                                        onClose();
                                    }}
                                    sx={{
                                        borderRadius: theme.spacing(1.5),
                                        "& .MuiCardActionArea-focusHighlight": { display: "none" },
                                    }}
                                >
                                    <TransportResourceCard
                                        appearance="legacy"
                                        kind="leg"
                                        title={title}
                                        subtitle={`${leg.originLabel ?? leg.originCountryCode ?? "-"} -> ${
                                            leg.destinationLabel ?? leg.destinationCountryCode ?? "-"
                                        }`}
                                        modeIcons={getTransportLegModeIcons(leg, transportModes)}
                                        selectable={false}
                                        meta={getTransportLegMeta(leg, t)}
                                    />
                                </CardActionArea>
                            );
                        })}
                    </Stack>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <ReferenceSearchDialog
            closeLabel={t("common.close", "Close")}
            maxHeight={532}
            onClose={onClose}
            onSearchChange={setSearch}
            open={open}
            searchPlaceholder={t("transport.add_leg.search_placeholder", "Search for a leg")}
            searchValue={search}
            title={t("transport.circuits.add_leg", "Add leg")}
        >
            <SelectionGrid
                columns={columns}
                emptyContent={
                    <FoodsTypography variant="body-regular">
                        {t("transport.add_leg.empty", "No leg matches the current search.")}
                    </FoodsTypography>
                }
                getRowId={(leg) => String(leg.id)}
                getRowAriaLabel={(leg) => getLegPresentation(leg).accessibleLabel}
                isLoading={false}
                maxVisibleRows={6}
                minTableWidth={736}
                onPageChange={() => undefined}
                onSelect={(leg) => {
                    onAdd(leg);
                    onClose();
                }}
                page={1}
                pageCount={0}
                rows={filteredLegs}
            />
        </ReferenceSearchDialog>
    );
};

export default TransportAddLegModal;
